import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, canUpload } from "../auth/AuthContext";
import { useT, LOCALES } from "../i18n";
import { useTheme, THEME_PREFS } from "../theme";
import { api } from "../api/client";
import type { Locale, ThemePref } from "../api/types";
import { Select } from "./Select";

interface HealthResponse {
  status: string;
  timestamp: string;
  version: string;
}

function useAppVersion(): string {
  const [version, setVersion] = useState<string>("");
  const fetchVersion = useCallback(async () => {
    try {
      const res = await fetch("/health");
      if (res.ok) {
        const data = (await res.json()) as HealthResponse;
        setVersion(data.version);
      }
    } catch {
      /* best-effort */
    }
  }, []);
  useEffect(() => {
    fetchVersion();
    const id = setInterval(fetchVersion, 60_000);
    return () => clearInterval(id);
  }, [fetchVersion]);
  return version;
}

export function Navbar() {
  const { user, logout, setUser } = useAuth();
  const { t, locale, setLocale } = useT();
  const { pref, setPref } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const appVersion = useAppVersion();
  const initials = (user?.displayName ?? user?.email ?? "?").charAt(0).toUpperCase();
  const isAdmin = user?.role === "admin";
  const showNavLinks = canUpload(user?.role);

  async function onLocaleChange(l: Locale) {
    setLocale(l);
    if (user) {
      try {
        const updated = await api.updateProfile({ locale: l });
        setUser(updated);
      } catch {
        /* best-effort; local state already updated */
      }
    }
  }

  async function onThemeChange(p: ThemePref) {
    setPref(p);
    if (user) {
      try {
        const updated = await api.updateProfile({ theme: p });
        setUser(updated);
      } catch {
        /* best-effort */
      }
    }
  }

  const langSelect = (
    <Select
      value={locale}
      options={LOCALES.map((l) => ({ value: l, label: t(`language.${l}`) }))}
      onChange={(v) => onLocaleChange(v as Locale)}
      ariaLabel={t("profile.language")}
      title={t("profile.language")}
      size="sm"
    />
  );

  const themeSelect = (
    <Select
      value={pref}
      options={THEME_PREFS.map((p) => ({ value: p, label: t(`theme.${p}`) }))}
      onChange={(v) => onThemeChange(v as ThemePref)}
      ariaLabel={t("profile.theme")}
      title={t("profile.theme")}
      size="sm"
    />
  );

  const authArea = user ? (
    <div className="flex items-center gap-3">
      <Link
        to="/profile"
        className="w-8 h-8 rounded-full bg-brand-red flex items-center justify-center text-sm font-semibold shrink-0"
        title={t("nav.profile")}
      >
        {initials}
      </Link>
      <button
        onClick={() => {
          logout();
          navigate("/login");
        }}
        className="text-sm text-brand-muted hover:text-brand-text whitespace-nowrap"
      >
        {t("nav.signOut")}
      </button>
    </div>
  ) : (
    <Link to="/login" className="text-sm text-brand-muted hover:text-brand-text whitespace-nowrap">
      {t("nav.signIn")}
    </Link>
  );

  const navLinks = showNavLinks && (
    <>
      <Link
        to="/my-videos"
        onClick={() => setMenuOpen(false)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-brand-hover transition-colors text-sm font-medium whitespace-nowrap"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H8V4h12v12zm-6-2l4-4-3-3-3 3-1-1-2 2 4 4 1-1z" opacity="0" />
          <path d="M10 8v6h4v-6h3l-5-5-5 5h3zM4 6v14h12v2H4c-1.1 0-2-.9-2-2V6h2zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
        </svg>
        {t("nav.myVideos")}
      </Link>
      {isAdmin && (
        <Link
          to="/users"
          onClick={() => setMenuOpen(false)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-brand-hover transition-colors text-sm font-medium whitespace-nowrap"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          {t("nav.users")}
        </Link>
      )}
      <Link
        to="/upload"
        onClick={() => setMenuOpen(false)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-hover hover:bg-brand-line transition-colors text-sm font-medium whitespace-nowrap"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 13v4h-4v-4H7l5-5 5 5h-3z M5 3h14v2H5V3z" opacity="0" />
          <path d="M10 8v6h4v-6h3l-5-5-5 5h3z M3 18h18v2H3v-2z" />
        </svg>
        {t("nav.upload")}
      </Link>
    </>
  );

  return (
    <header className="sticky top-0 z-30 bg-brand-dark border-b border-brand-line">
      <div className="flex items-center gap-4 px-4 h-14">
        <Link to="/" className="flex items-center gap-1.5 shrink-0">
          <span className="text-brand-red text-2xl leading-none">&#9656;</span>
          <span className="font-semibold text-lg tracking-tight">HelixStream</span>
        </Link>

        <div className="flex-1" />

        {/* Desktop nav links */}
        {showNavLinks && (
          <div className="hidden sm:flex items-center gap-1">{navLinks}</div>
        )}

        {/* Desktop language + theme */}
        <div className="hidden sm:flex items-center gap-2">
          {langSelect}
          {themeSelect}
        </div>

        {/* Desktop auth area */}
        <div className="hidden sm:flex items-center">{authArea}</div>

        {/* Desktop version */}
        {appVersion && (
          <div className="hidden sm:block text-xs text-brand-muted self-end">v{appVersion}</div>
        )}

        {/* Mobile: compact language + hamburger */}
        <div className="flex sm:hidden items-center gap-2">
          {langSelect}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={menuOpen}
            className="p-2 rounded-lg hover:bg-brand-hover transition-colors"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              {menuOpen ? (
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              ) : (
                <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile dropdown panel */}
      {menuOpen && (
        <div className="sm:hidden border-t border-brand-line bg-brand-dark px-4 py-3 space-y-3">
          {showNavLinks && <div className="flex flex-col gap-1">{navLinks}</div>}
          <div className="flex items-center gap-3 pt-2 border-t border-brand-line">
            {themeSelect}
            {authArea}
          </div>
          {appVersion && (
            <div className="text-center text-xs text-brand-muted pt-1">{appVersion}</div>
          )}
        </div>
      )}
    </header>
  );
}